// Lightweight GA4 + Meta Pixel wrapper. Every call is a no-op when the
// corresponding ID is not configured, so the app never breaks because of tracking.
const GA4_ID = process.env.VUE_APP_GA4_ID;
const META_PIXEL_ID = process.env.VUE_APP_META_PIXEL_ID;

let initialized = false;

// GA4 and the Pixel weigh ~400 KB together: they load once the app is on screen and the browser is idle.
// Calls made before that wait in dataLayer / fbq.queue, so nothing is lost.
function whenIdle(fn) {
  const run = () => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 4000 }) : setTimeout(fn, 1500));
  if (document.readyState === "complete") run();
  else window.addEventListener("load", run, { once: true });
}

function loadScript(src) {
  whenIdle(() => {
    const s = document.createElement("script");
    s.async = true;
    s.src = src;
    document.head.appendChild(s);
  });
}

export function initAnalytics() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  if (GA4_ID) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", GA4_ID, { send_page_view: false });
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
  }

  if (META_PIXEL_ID && !window.fbq) {
    const fbq = function () {
      fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;
    loadScript("https://connect.facebook.net/en_US/fbevents.js");
    window.fbq("init", META_PIXEL_ID);
  }
}

// Our own copy of the usage events (backend ProductEvent), so we can read them without GA. Only for signed-in
// users (patient-facing pages have no token), in small batches, never with patient data in the properties.
const API_BASE = process.env.VUE_APP_API_BASE_URL || "";
const queue = [];
let flushTimer = null;
let currentPage = "";
let errorsSent = 0;

function authHeaders() {
  const token = localStorage.getItem("ordexa/headers/access-token");
  if (!token) return null;
  return {
    "access-token": token,
    client: localStorage.getItem("ordexa/headers/client") || "",
    uid: localStorage.getItem("ordexa/headers/uid") || "",
    expiry: localStorage.getItem("ordexa/headers/expiry") || "",
    "token-type": localStorage.getItem("ordexa/headers/token-type") || "Bearer",
  };
}

function flush() {
  clearTimeout(flushTimer);
  flushTimer = null;
  const headers = authHeaders();
  if (!queue.length) return;
  if (!headers) {
    queue.length = 0;
    return;
  }
  const events = queue.splice(0, 30);
  try {
    // keepalive lets the last batch leave when the tab is closing
    fetch(`${API_BASE}/api/v1/events`, {
      method: "POST",
      keepalive: true,
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ events }),
    }).catch(() => {});
  } catch (e) {
    // ignore
  }
  if (queue.length) flushTimer = setTimeout(flush, 1000);
}

function record(name, props = {}) {
  try {
    if (!localStorage.getItem("ordexa/headers/access-token")) return;
    const clean = {};
    Object.keys(props || {}).forEach((k) => {
      const v = props[k];
      if (typeof v === "number" || typeof v === "boolean" || typeof v === "string") clean[k] = v;
    });
    queue.push({ n: name, p: currentPage, props: clean, t: Date.now() });
    if (queue.length >= 20) flush();
    else if (!flushTimer) flushTimer = setTimeout(flush, 10000);
  } catch (e) {
    // ignore
  }
}

if (typeof window !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  // Errors people hit but never report (a few per page load at most)
  const reportError = (message, source) => {
    if (errorsSent >= 5 || !message) return;
    errorsSent += 1;
    record("client_error", { error: String(message).slice(0, 150), source: String(source || "").slice(0, 60) });
  };
  window.addEventListener("error", (e) => reportError(e.message, e.filename && e.filename.split("/").pop()));
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason || {};
    // Failed API calls are already handled and logged by the server
    if (r.isAxiosError || r.response) return;
    reportError(r.message || String(r), "promise");
  });
}

// pattern: the route as declared ("/customers/:id"), so screens group together without ids
export function trackPageView(path, pattern) {
  currentPage = pattern || String(path || "").split("?")[0];
  // Phone vs computer, and opened from the installed app's icon, to read the funnels by device
  const standalone = typeof window !== "undefined" && ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true);
  record("page_view", { mobile: typeof window !== "undefined" && window.innerWidth <= 768, app: standalone });
  try {
    if (window.gtag && GA4_ID) {
      window.gtag("event", "page_view", {
        page_location: window.location.origin + path,
        page_path: path,
        page_title: document.title,
      });
    }
    if (window.fbq && META_PIXEL_ID) window.fbq("track", "PageView");
  } catch (e) {
    // ignore
  }
}

const META_EVENTS = {
  sign_up: "CompleteRegistration",
  begin_checkout: "InitiateCheckout",
  purchase: "Purchase",
};

export function track(event, params = {}) {
  record(event, params);
  try {
    if (window.gtag && GA4_ID) window.gtag("event", event, params);
    if (window.fbq && META_PIXEL_ID && META_EVENTS[event]) {
      window.fbq("track", META_EVENTS[event], params);
    }
  } catch (e) {
    // ignore
  }
}

// First-touch attribution captured by the marketing site (or the app itself).
export function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    // ab: site experiment version the visitor saw ("hero1-b"), see backend docs/EXPERIMENTOS.md
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid", "ab"];
    const found = {};
    keys.forEach((k) => {
      if (params.get(k)) found[k] = params.get(k);
    });
    // Visitors who come straight to the app (ChatGPT, for instance) skip the site:
    // keep the referrer when there is no utm, and the app page they landed on.
    if (!found.utm_source) {
      let host = "";
      try {
        host = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, "") : "";
      } catch (e) {
        host = "";
      }
      if (host && !host.endsWith("ordexa.com.br")) {
        found.utm_source = host;
        found.utm_medium = "referral";
      }
    }
    if (found.utm_source && !found.utm_campaign) {
      found.utm_campaign = `app_${window.location.pathname.replace(/^\/|\/$/g, "").split("/")[0] || "home"}`.slice(0, 40);
    }
    if (Object.keys(found).length && !localStorage.getItem("ordexa/attribution")) {
      localStorage.setItem("ordexa/attribution", JSON.stringify(found));
    }
  } catch (e) {
    // ignore
  }
}

export function getAttribution() {
  try {
    return JSON.parse(localStorage.getItem("ordexa/attribution") || "{}");
  } catch (e) {
    return {};
  }
}
